import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import DriveFileRenameOutlineRounded from '@mui/icons-material/DriveFileRenameOutlineRounded';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  stepLabelClasses,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { Button as SharedButton, Loader } from '@/shared/ui';
import type { SavedPresetRecord } from '../../model/types';
import { PresetForm } from '../PresetForm/PresetForm';
import { useSavedPresetsContext } from '../SavedPresetsContext/SavedPresetsContext';
import { SelectPreset } from './SelectPreset';

const Button = styled(SharedButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
}));

interface ContextState {
  openSavePresetDialog: () => void;
}

const Context = createContext<ContextState | null>(null);

enum ActionType {
  create = 'create',
  update = 'update',
}

const ACTION_CARDS = [
  {
    type: ActionType.create,
    label: 'Create New',
    description: 'Save as a brand new preset',
    Icon: AddCircleOutlineRounded,
  },
  {
    type: ActionType.update,
    label: 'Update Existing',
    description: 'Overwrite a saved preset',
    Icon: DriveFileRenameOutlineRounded,
  },
] as const;

interface Props {
  children: ReactNode;
}

export function SavePresetProvider({ children }: Props) {
  const { presets, updatePreset, createPreset } = useSavedPresetsContext();
  const [actionType, setActionType] = useState<ActionType>(ActionType.create);
  const [activeStep, setActiveStep] = useState(0);
  const [presetToUpdate, setPresetToUpdate] = useState<SavedPresetRecord>();
  const [viewName, setViewName] = useState('');
  const [viewNameError, setViewNameError] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [openSavePresetDialog, setOpenSavePresetDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setPresetToUpdate(presets[0]);
  }, [presets]);

  const handleNameChange = (value: string) => {
    setViewName(value);
    setViewNameError('');
  };

  const handleOpenSavePresetDialog = () => {
    setActiveStep(0);
    setActionType(ActionType.create);
    setViewName('');
    setIsDefault(false);
    setViewNameError('');
    setOpenSavePresetDialog(true);
  };

  const handleCloseSavePresetDialog = () => {
    setOpenSavePresetDialog(false);
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleEditStep = (step: number) => {
    setActiveStep(step);
    setViewNameError('');
  };

  const handleActionTypeChange = (newType: ActionType) => {
    setActionType(newType);
    setViewNameError('');
    if (newType === ActionType.create) {
      setViewName('');
      setIsDefault(false);
    } else if (presetToUpdate) {
      setViewName(presetToUpdate.name);
      setIsDefault(presetToUpdate.isDefault);
    }
  };

  const handlePresetChange = (preset: SavedPresetRecord) => {
    setPresetToUpdate(preset);
    setViewName(preset.name);
    setIsDefault(preset.isDefault);
    setViewNameError('');
  };

  const validateName = () => {
    if (!viewName) {
      setViewNameError('Preset name is required');
      return false;
    }
    return true;
  };

  const handleCreate = () => {
    if (!validateName()) {
      return;
    }

    setIsLoading(true);
    createPreset(viewName, isDefault)
      .then(handleCloseSavePresetDialog)
      .finally(() => setIsLoading(false));
  };

  const handleUpdate = () => {
    if (!validateName()) {
      return;
    }

    setIsLoading(true);
    updatePreset(presetToUpdate!.id, { name: viewName, isDefault })
      .then(handleCloseSavePresetDialog)
      .finally(() => setIsLoading(false));
  };

  const stepLabelSx = {
    p: 0,
    [`& .${stepLabelClasses.label}`]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  };

  return (
    <Context.Provider
      value={{ openSavePresetDialog: handleOpenSavePresetDialog }}
    >
      {children}
      <Dialog
        PaperProps={{
          sx: {
            borderRadius: 4,
            width: 470,
          },
        }}
        open={openSavePresetDialog}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            p: 2,
          }}
        >
          <DialogTitle
            sx={{
              p: 0,
              fontWeight: 500,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: 0,
            }}
          >
            Save Preset
          </DialogTitle>
          <IconButton size="small" onClick={handleCloseSavePresetDialog}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent dividers sx={{ p: 0, position: 'relative' }}>
          {presets.length === 0 ? (
            <Box
              sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <PresetForm
                name={viewName}
                isDefault={isDefault}
                nameError={viewNameError}
                onNameChange={handleNameChange}
                onIsDefaultChange={setIsDefault}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreate}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCloseSavePresetDialog}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Stepper
              activeStep={activeStep}
              orientation="vertical"
              connector={<></>}
            >
              {/* Step 0: Choose action */}
              <Step sx={{ p: 2 }}>
                <StepLabel
                  sx={stepLabelSx}
                  optional={
                    activeStep > 0 ? (
                      <Typography variant="caption">
                        <b>Action:</b>{' '}
                        {actionType === ActionType.create
                          ? 'Create New'
                          : 'Update Existing'}
                      </Typography>
                    ) : null
                  }
                >
                  Choose action
                  {activeStep > 0 && (
                    <Button
                      sx={{ p: 0.5 }}
                      variant="text"
                      onClick={() => handleEditStep(0)}
                    >
                      Edit
                    </Button>
                  )}
                </StepLabel>
                <StepContent sx={{ borderLeft: 'none', pr: 0 }}>
                  <Grid container spacing={1.25} sx={{ mt: 0.5 }}>
                    {ACTION_CARDS.map(({ type, label, description, Icon }) => {
                      const isSelected = actionType === type;
                      return (
                        <Grid key={type} item xs={6}>
                          <ButtonBase
                            onClick={() => {
                              handleActionTypeChange(type);
                              handleNext();
                            }}
                            sx={{
                              width: '100%',
                              border: '2px solid',
                              borderColor: isSelected
                                ? 'primary.main'
                                : 'divider',
                              borderRadius: 2.5,
                              p: 1.5,
                              flexDirection: 'column',
                              gap: 0.75,
                              textAlign: 'center',
                              backgroundColor: isSelected
                                ? '#E7ECFA'
                                : 'transparent',
                              transition:
                                'border-color 0.15s, background-color 0.15s',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: '#E7ECFA',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: isSelected
                                  ? 'primary.main'
                                  : 'action.hover',
                                color: isSelected ? '#fff' : 'text.secondary',
                              }}
                            >
                              <Icon fontSize="small" />
                            </Box>
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                lineHeight: 1.2,
                              }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                lineHeight: 1.35,
                              }}
                            >
                              {description}
                            </Typography>
                          </ButtonBase>
                        </Grid>
                      );
                    })}
                  </Grid>
                </StepContent>
              </Step>

              {/* Step 1: Preset details (Create) or Select preset (Update) */}
              <Step
                sx={{
                  p: 2,
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <StepLabel
                  sx={stepLabelSx}
                  optional={
                    actionType === ActionType.update && activeStep > 1 ? (
                      <Typography variant="caption">
                        <b>Preset:</b> {presetToUpdate?.name}
                      </Typography>
                    ) : null
                  }
                >
                  {actionType === ActionType.update
                    ? 'Select preset'
                    : 'Preset details'}
                  {actionType === ActionType.update && activeStep > 1 && (
                    <Button
                      sx={{ p: 0.5 }}
                      variant="text"
                      onClick={() => handleEditStep(1)}
                    >
                      Edit
                    </Button>
                  )}
                </StepLabel>
                <StepContent sx={{ borderLeft: 'none', pr: 0 }}>
                  <Box sx={{ mt: 1 }}>
                    {actionType === ActionType.create ? (
                      <>
                        <PresetForm
                          name={viewName}
                          isDefault={isDefault}
                          nameError={viewNameError}
                          onNameChange={handleNameChange}
                          onIsDefaultChange={setIsDefault}
                        />
                        <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreate}
                          >
                            Save
                          </Button>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleCloseSavePresetDialog}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <>
                        <SelectPreset
                          presets={presets}
                          value={presetToUpdate}
                          onChange={handlePresetChange}
                        />
                        <Button
                          sx={{ mt: 1.5 }}
                          disabled={!presetToUpdate}
                          onClick={handleNext}
                        >
                          Next
                        </Button>
                      </>
                    )}
                  </Box>
                </StepContent>
              </Step>

              {/* Step 2: Update details (Update path only) */}
              {actionType === ActionType.update && (
                <Step
                  sx={{
                    p: 2,
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <StepLabel sx={stepLabelSx}>Update details</StepLabel>
                  <StepContent sx={{ borderLeft: 'none', pr: 0 }}>
                    <Box sx={{ mt: 1 }}>
                      <PresetForm
                        name={viewName}
                        isDefault={isDefault}
                        nameError={viewNameError}
                        onNameChange={handleNameChange}
                        onIsDefaultChange={setIsDefault}
                      />
                      <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleUpdate}
                        >
                          Update
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={handleCloseSavePresetDialog}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  </StepContent>
                </Step>
              )}
            </Stepper>
          )}
          <Loader active={isLoading} />
        </DialogContent>
      </Dialog>
    </Context.Provider>
  );
}

export const useSavePresetContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error(
      'useSavePresetContext must be used within a SavePresetProvider',
    );
  }

  return context;
};
